//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Avatar from '@components/atoms/avatar/Avatar';
import { Icon } from '@components/atoms/icon/Icon';
import Text from '@components/atoms/text/Text';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { ITwitterUserSuggestion } from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { Mixins } from '@theme/mixins';
import { withTracking } from '@utils/withTracking';

interface Props {
  handleSuggestionClick: (mention: ITwitterUserSuggestion) => void;
  suggestion: ITwitterUserSuggestion;
}

export const TwitterUserSuggestion = ({
  handleSuggestionClick,
  suggestion,
}: Props) => {
  const smartInputContext = useSmartInputContext();

  return (
    <SuggestionWrapper role='list-item'>
      <Suggestion
        onClick={() => {
          handleSuggestionClick(suggestion);
        }}
        trackingId={getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.MentionsSuggestionsAddMention,
        )}
      >
        <ContentWrapper>
          <Avatar
            imageUrl={suggestion.avatarUrl}
            size={50}
            username={suggestion.username}
          />
          <TextWrapper>
            <NameWrapper>
              <NameText mixin={Mixins.Text.UiCopyBold}>
                {suggestion.name}
              </NameText>
              {suggestion.isVerified && <VerifiedIcon name='verified' />}
            </NameWrapper>
            <Text mixin={Mixins.Text.UiCopySecondary}>
              @{suggestion.username}
            </Text>
          </TextWrapper>
        </ContentWrapper>
      </Suggestion>
    </SuggestionWrapper>
  );
};

const SuggestionWrapper = styled.div`
  flex: none;
`;

const Suggestion = withTracking(
  styled.button(
    ({ theme }) => `
    width: 100%;
    background-color: ${theme.colors.white};
    border: 1px solid transparent;
    cursor: pointer;
    padding: ${theme.spacings[2]} ${theme.spacings[3]};

    &:hover,
    &:focus {
      background-color: ${theme.colors.gray0};
      text-decoration: underline;
    }
    
    &:focus {
      outline: none;
      border: 1px solid ${theme.colors.gray4};
    }
  `,
  ),
);

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TextWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    margin-left: ${theme.spacings[2]};
  `,
);

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NameText = styled(Text)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
  `,
);

const VerifiedIcon = styled(Icon)`
  /* Color specific to the Twitter verified icon*/
  color: #1da1f2 !important;
`;
